import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { showImageViewer, showVideoViewer } from "@organisms/alert";
import { getFileSizeTranslation } from "@utils/files";

import type { IHeaderViewerProps } from "@components/modals/imageViewer";
import type { IFileObject } from "@interfaces";

export type IFileViewerType = File | IFileObject;

function getFileDataInitValue(file: IFileViewerType): IFileObject {
  /**
   * indicate prop.file is File or IFileObject
   */
  const isFile = file instanceof File;

  if (isFile)
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      url: globalThis.URL.createObjectURL(file),
      id: crypto.randomUUID(),
    };

  return file;
}

/**
 *  File reader/loader & viewer API
 * @param file  The file itself or the IFileObject
 * @returns
 */
export function useFileViewer(
  file: IFileViewerType,
  header?: IHeaderViewerProps,
) {
  /**
   * indicate file type is image or not
   * @param type file type
   * @returns
   */

  const { t } = useTranslation("component");

  const fileData = getFileDataInitValue(file);

  const fileType = file?.type;

  const isImage = fileType?.includes("image") || false;
  const isVideo = fileType?.includes("video") || false;
  const isAudio = fileType?.includes("audio") || false;
  const isPdf = fileType?.includes("pdf") || false;

  const canShow = isVideo || isImage;

  useEffect(() => {
    return () => {
      if (fileData?.url) globalThis.URL.revokeObjectURL(fileData.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   *
   * @returns show the image in a modal
   */
  function show() {
    if (fileData) {
      if (isImage)
        showImageViewer({
          title: fileData.name,
          src: fileData.url,
          date: getFileSizeTranslation(t, fileData.size),
          header,
        });
      if (isVideo)
        showVideoViewer({
          title: fileData.name,
          src: fileData.url,
          date: getFileSizeTranslation(t, fileData.size),
        });
    }
  }

  return {
    isImage,
    isVideo,
    isPdf,
    isAudio,
    canShow,
    fileData,
    show,
  };
}
